import { Loading as QuickCompaniesOverviewLoading } from '@/_pages/companies/shared/widgets/widget-companies/widget-loading';
import { Loading as TopCompetitorsSnapshotLoading } from '@/_pages/competitors/shared/widgets/widget-competitors/widget-loading';
import { UpcomingConferencesEventsWidgetLoading } from '@/_pages/conference-events/shared/widgets/widget-upcoming-conferences-events/widget-loading';
import { ExpiringDomainsAssociatedWithAgreementsWidgetLoading } from '@/_pages/domains/shared/widgets/widget-expiring-domains-associated-with-agreements/widget-loading';
import { AnnualHistoricalSalesLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-annual-historical-sales/loading';
import { FinancialSnapshotsLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-financial-snapshot-items/widget-loading';
import { MonthlySalesSummaryLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-monthly-sales-summary/loading';
import { RecurringRevenueVarianceLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-recurring-revenue-variance/loading';
import { CashAvailableOnHandLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-snapshot-cash-available-on-hand/loading';
import { CashReservesOnHandLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-snapshot-cash-reserves-on-hand/loading';
import { CreditCardDebtLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-snapshot-credit-card-debt/loading';
import { CreditCardFundsAvailableLoading } from '@/_pages/financial-snapshot/shared/widgets/widget-snapshot-credit-card-funds-available/loading';
import { Loading as InventoryAssetValueLoading } from '@/_pages/inventories/shared/widgets/widget-inventory-asset-value/widget-loading';
import { InventoryBelowMinimumStockLoading } from '@/_pages/inventories/shared/widgets/widget-inventory-minimum-stock/widget-loading';
import {
  dashboardWidgetsPermissionConfig,
  type WidgetModelName,
} from '@/_shared/routes-widgets/configs/permissible-widget-keys';

import type { WidgetConfig } from './types';
import {
  AgreementCloseConversion,
  AgreementCloseConversionLoading,
  AgreementsDashboardCard,
  AnnualHistoricalSales,
  CommissionDashboard,
  CommissionDashboardLoading,
  CompaniesDashboardCard,
  ContactsDashboardCard,
  ContactUpcomingBirthdaysDashboardCard,
  ExpiringDomainsAssociatedWithAgreementsWidget,
  FinancialSnapshotItems,
  GeographicAgreementMap,
  GeographicAgreementMapLoading,
  ImplementationDuration,
  ImplementationDurationLoading,
  InventoryAssetValue,
  InventoryBelowMinimumStock,
  MileageDashboard,
  MileageDashboardLoading,
  MonthlySalesSummary,
  PartnerRevenueTailPayouts,
  PartnerRevenueTailPayoutsLoading,
  QuickCompaniesOverview,
  RecurringRevenueVariance,
  SnapshotCashAvailableOnHand,
  SnapshotCashReservesOnHand,
  SnapshotCreditCardDebt,
  SnapshotCreditCardFundsAvailable,
  TimeOffCalendarWidget,
  TimeOffCalendarWidgetLoading,
  TopCompetitorsSnapshot,
  UpcomingConferencesEventsWidget,
} from './widgets-config-map';

// Map widget keys to their configuration
export const WIDGET_CONFIG: Record<WidgetModelName, WidgetConfig> = {
  recent_agreements: {
    ...dashboardWidgetsPermissionConfig.recent_agreements,
    component: AgreementsDashboardCard,
    withFooter: true,
  },
  recent_companies: {
    ...dashboardWidgetsPermissionConfig.recent_companies,
    component: CompaniesDashboardCard,
    withFooter: true,
  },
  recent_contacts: {
    ...dashboardWidgetsPermissionConfig.recent_contacts,
    component: ContactsDashboardCard,
    withFooter: true,
  },
  upcoming_birthdays: {
    ...dashboardWidgetsPermissionConfig.upcoming_birthdays,
    component: ContactUpcomingBirthdaysDashboardCard,
  },
  cash_available_on_hand: {
    ...dashboardWidgetsPermissionConfig.cash_available_on_hand,
    component: SnapshotCashAvailableOnHand,
    loader: <CashAvailableOnHandLoading />,
  },
  cash_reserves_on_hand: {
    ...dashboardWidgetsPermissionConfig.cash_reserves_on_hand,
    component: SnapshotCashReservesOnHand,
    loader: <CashReservesOnHandLoading />,
  },
  credit_card_funds_available: {
    ...dashboardWidgetsPermissionConfig.credit_card_funds_available,
    component: SnapshotCreditCardFundsAvailable,
    loader: <CreditCardFundsAvailableLoading />,
  },
  credit_card_debt: {
    ...dashboardWidgetsPermissionConfig.credit_card_debt,
    component: SnapshotCreditCardDebt,
    loader: <CreditCardDebtLoading />,
  },
  financial_snapshot: {
    ...dashboardWidgetsPermissionConfig.financial_snapshot,
    component: FinancialSnapshotItems,
    loader: <FinancialSnapshotsLoading />,
  },
  monthly_sales_summary: {
    ...dashboardWidgetsPermissionConfig.monthly_sales_summary,
    component: MonthlySalesSummary,
    loader: <MonthlySalesSummaryLoading layout />,
  },
  annual_historical_sales: {
    ...dashboardWidgetsPermissionConfig.annual_historical_sales,
    component: AnnualHistoricalSales,
    loader: <AnnualHistoricalSalesLoading layout />,
  },
  recurring_revenue_variance: {
    ...dashboardWidgetsPermissionConfig.recurring_revenue_variance,
    component: RecurringRevenueVariance,
    loader: <RecurringRevenueVarianceLoading layout />,
  },
  inventory_below_minimum_stock: {
    ...dashboardWidgetsPermissionConfig.inventory_below_minimum_stock,
    component: InventoryBelowMinimumStock,
    loader: <InventoryBelowMinimumStockLoading />,
  },
  inventory_asset_value: {
    ...dashboardWidgetsPermissionConfig.inventory_asset_value,
    component: InventoryAssetValue,
    loader: <InventoryAssetValueLoading />,
  },

  upcoming_conferences_events: {
    ...dashboardWidgetsPermissionConfig.upcoming_conferences_events,
    component: UpcomingConferencesEventsWidget,
    loader: <UpcomingConferencesEventsWidgetLoading />,
  },
  quick_companies_overview: {
    ...dashboardWidgetsPermissionConfig.quick_companies_overview,
    component: QuickCompaniesOverview,
    loader: <QuickCompaniesOverviewLoading />,
  },
  expiring_domains_agreements: {
    ...dashboardWidgetsPermissionConfig.expiring_domains_agreements,
    component: ExpiringDomainsAssociatedWithAgreementsWidget,
    loader: <ExpiringDomainsAssociatedWithAgreementsWidgetLoading />,
  },
  top_competitors_snapshot: {
    ...dashboardWidgetsPermissionConfig.top_competitors_snapshot,
    component: TopCompetitorsSnapshot,
    loader: <TopCompetitorsSnapshotLoading />,
  },
  geographic_agreement_map: {
    ...dashboardWidgetsPermissionConfig.geographic_agreement_map,
    component: GeographicAgreementMap,
    loader: <GeographicAgreementMapLoading />,
  },
  time_off_calendar: {
    ...dashboardWidgetsPermissionConfig.time_off_calendar,
    component: TimeOffCalendarWidget,
    loader: <TimeOffCalendarWidgetLoading mode="day" />,
  },
  agreement_close_conversion: {
    ...dashboardWidgetsPermissionConfig.agreement_close_conversion,
    component: AgreementCloseConversion,
    loader: <AgreementCloseConversionLoading />,
  },
  implementation_duration: {
    ...dashboardWidgetsPermissionConfig.implementation_duration,
    component: ImplementationDuration,
    loader: <ImplementationDurationLoading />,
  },
  commission_dashboard: {
    ...dashboardWidgetsPermissionConfig.commission_dashboard,
    component: CommissionDashboard,
    loader: <CommissionDashboardLoading />,
  },
  mileage_tracking: {
    ...dashboardWidgetsPermissionConfig.mileage_tracking,
    component: MileageDashboard,
    loader: <MileageDashboardLoading />,
  },
  partner_payouts: {
    ...dashboardWidgetsPermissionConfig.partner_payouts,
    component: PartnerRevenueTailPayouts,
    loader: <PartnerRevenueTailPayoutsLoading />,
  },
};
